<div class="privacy-policy-container">

  <!-- Header with Logo -->
  <header class="policy-header">
    <img src="assets/ScholarX_Logo_v2 1.svg" alt="ScholarX Logo" class="policy-logo" />
  </header>

  <h1>Privacy Policy</h1>
  <p><strong>Effective Date:</strong> 8 October 2025</p>

  <p>
    This Privacy Policy applies to <strong>ScholarX.ai</strong>, an AI-driven educational platform
    developed and operated by <strong>Irish Taylor Private Limited</strong> (“we”, “us”, or “our”).
    We are committed to protecting your personal data and complying with the
    <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong> and other applicable Indian laws.
  </p>

  <p>
    By accessing or using ScholarX.ai, you consent to the collection, use, storage,
    and processing of your personal data as described in this Privacy Policy.
  </p>

  <h2>1. Introduction</h2>
  <p>
    ScholarX is an AI-powered learning platform designed to support students, teachers,
    parents, and educational institutions through personalized tutoring, analytics,
    and intelligent learning insights.
  </p>

  <h2>2. Definitions</h2>
  <ul>
    <li><strong>Personal Data:</strong> Any data about an identifiable individual.</li>
    <li><strong>Data Principal:</strong> The individual (student, parent, or teacher) to whom the data relates.</li>
    <li><strong>Data Fiduciary:</strong> Irish Taylor Private Limited.</li>
    <li><strong>Processing:</strong> Collection, storage, use, disclosure, or deletion of personal data.</li>
    <li><strong>Child:</strong> An individual below 18 years of age.</li>
  </ul>

  <h2>3. Applicability</h2>
  <p>This policy applies to:</p>
  <ul>
    <li>Students using ScholarX for learning, assessments, and AI assistance</li>
    <li>Teachers and institutions using ScholarX for academic or administrative purposes</li>
    <li>Parents or guardians accessing student insights and reports</li>
  </ul>

  <h2>4. Data We Collect</h2>

  <h3>a. Data Provided by Users</h3>
  <ul>
    <li>Name, age, email address, and mobile number</li>
    <li>Student grade, school, curriculum (CBSE, ICSE, etc.)</li>
    <li>Login credentials (securely hashed and encrypted)</li>
    <li>Parent or guardian information for minors</li>
  </ul>

  <h3>b. Data Collected Automatically</h3>
  <ul>
    <li>Device, browser, and operating system details</li>
    <li>IP address and approximate location</li>
    <li>Platform usage logs, interaction history, and AI chat transcripts</li>
  </ul>

  <h3>c. Data Generated by AI Interactions</h3>
  <ul>
    <li>Student responses, performance metrics, and progress analytics</li>
    <li>AI-generated recommendations, insights, and learning paths</li>
  </ul>

  <h2>5. Purpose of Processing</h2>
  <ul>
    <li>Deliver personalized AI-based tutoring and learning support</li>
    <li>Generate academic insights and performance analytics</li>
    <li>Improve AI models for educational accuracy and relevance</li>
    <li>Maintain platform security, safety, and integrity</li>
    <li>Comply with legal and regulatory obligations</li>
  </ul>

  <p><strong>We do not sell or trade personal data for commercial gain.</strong></p>

  <h2>6. Legal Basis for Processing</h2>
  <ul>
    <li>Consent of the Data Principal or guardian (for minors)</li>
    <li>Performance of educational contracts with institutions</li>
    <li>Compliance with Indian legal obligations</li>
    <li>Legitimate educational interests</li>
  </ul>

  <h2>7. Consent Management</h2>
  <ul>
    <li>Consent may be withdrawn at any time by writing to <strong>info@scholarx.ai</strong></li>
    <li>Parental consent is mandatory for users below 18 years</li>
    <li>Upon withdrawal, data will be anonymized or deleted unless legally required</li>
  </ul>

  <h2>8. Data Retention and Deletion</h2>
  <p>
    Data is retained only for as long as necessary for educational purposes.
    Upon account deletion or consent withdrawal, data will be erased or anonymized
    within <strong>30 days</strong>, unless retention is required by law.
  </p>

  <h2>9. Data Security</h2>
  <ul>
    <li>End-to-end encryption (data at rest and in transit)</li>
    <li>Secure access controls and multi-factor authentication</li>
    <li>AI governance with anonymized datasets</li>
    <li>Regular security audits under Rothcrest Cybersecurity</li>
  </ul>

  <p>
    In the event of a data breach, affected users and the Data Protection Board of India
    will be notified as required under the DPDP Act.
  </p>

  <h2>10. Data Sharing and Transfers</h2>
  <p>Personal data may be shared only with:</p>
  <ul>
    <li>Authorized educational partners under strict confidentiality</li>
    <li>Cloud service providers (AWS, Google Cloud, Microsoft Azure)</li>
    <li>Government or regulatory authorities upon lawful request</li>
  </ul>

  <h2>11. Children’s Data</h2>
  <ul>
    <li>Parental or institutional consent is mandatory</li>
    <li>No targeted advertising or behavioral profiling</li>
    <li>Privacy-filtered AI interaction logs</li>
  </ul>

  <h2>12. Rights of Data Principals</h2>
  <p>Under the DPDP Act, you have the right to:</p>
  <ul>
    <li>Access and confirm your personal data</li>
    <li>Request correction or deletion</li>
    <li>Withdraw consent</li>
    <li>Nominate another individual to exercise your rights</li>
  </ul>

  <p>Requests can be sent to <strong>info@scholarx.ai</strong></p>

  <h2>13. Grievance Redressal</h2>
  <p>
    <strong>Data Protection Officer (DPO)</strong><br />
    Irish Taylor Private Limited<br />
    Email: info@scholarx.ai<br />
    Address: Delhi, India
  </p>

  <p>
    If unresolved, complaints may be escalated to the
    <strong>Data Protection Board of India (DPBI)</strong>.
  </p>

  <h2>14. Policy Updates</h2>
  <p>
    This Privacy Policy may be updated periodically.
    Updates will be posted on
    <a href="https://www.scholarx.ai/privacy" target="_blank">https://www.scholarx.ai/privacy</a>
    with a revised effective date.
  </p>

  <h2>15. Governing Law</h2>
  <p>
    This policy is governed by the laws of India, with exclusive jurisdiction
    in the courts of Delhi.
  </p>

  <h2>16. Contact</h2>
  <p>
    📧 Email: <strong>info@scholarx.ai</strong><br />
    🌐 Website: <a href="https://www.scholarx.ai" target="_blank">https://www.scholarx.ai</a>
  </p>

</div>
